// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import ConfigErrorBoundary from './components/ConfigErrorBoundary';

// FAIL FAST: Wait for configuration validation to complete before React starts
function waitForConfigValidation() {
  return new Promise((resolve, reject) => {
    const maxWaitTime = 10000; // 10 seconds max wait
    const startTime = Date.now();

    function checkValidation() {
      // Check if config is loaded
      if (!window.CONFIG) {
        if (Date.now() - startTime > maxWaitTime) {
          console.error('FATAL: window.CONFIG is not defined after timeout. Application cannot start.');
          document.body.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, #ee5a6f 0%, #f29263 100%); padding: 20px;">
              <div style="background: white; padding: 40px; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); max-width: 600px; text-align: center;">
                <div style="font-size: 60px; margin-bottom: 20px;">🚫</div>
                <h1 style="color: #2c3e50; margin-bottom: 10px;">Configuration Not Loaded</h1>
                <p style="color: #7f8c8d; margin-bottom: 30px;">The application configuration failed to load. Please refresh the page or contact support.</p>
                <button onclick="location.reload()" style="padding: 12px 30px; background: #ff6b6b; color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer;">Retry</button>
              </div>
            </div>
          `;
          reject(new Error('Configuration not loaded'));
          return;
        }
        // Config not loaded yet, wait and retry
        setTimeout(checkValidation, 50);
        return;
      }

      // Check if validation is complete
      if (!window.ConfigValidator || !window.ConfigValidator.isValidated()) {
        if (Date.now() - startTime > maxWaitTime) {
          console.error('FATAL: Configuration was not validated after timeout. Application cannot start.');
          reject(new Error('Configuration not validated'));
          return;
        }
        // Validation not complete yet, wait and retry
        setTimeout(checkValidation, 50);
        return;
      }

      // Both config loaded and validated!
      console.log('✅ Configuration validation complete, starting React app...');
      resolve();
    }

    // Start checking immediately
    checkValidation();
  });
}

// Important: basename="/calculator" means all paths in Route and useNavigate should be relative
// without /calculator prefix, as BrowserRouter will add it automatically.

// Conditional basename for development vs production
const basename = process.env.NODE_ENV === 'development' ? '' : '/calculator';

// Add app version info to global object for debugging
window.QUER_VERSION = {
  build: '1.0.0',
  env: process.env.NODE_ENV,
  basename: basename,
  date: new Date().toISOString(),
  pipeline_test: 'v2025-07-16',
  failFast: true // Mark that we're using fail-fast configuration
};

// Start the application after configuration validation
async function startApplication() {
  try {
    console.log('🚀 React Calculator v2025-07-16 (FAIL FAST) loading...');

    // Wait for configuration validation to complete
    await waitForConfigValidation();

    console.log('🔧 Router basename:', basename);
    console.log('✅ Configuration validated:', window.CONFIG.environment);

    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <ConfigErrorBoundary>
        <BrowserRouter basename={basename}>
          <App />
        </BrowserRouter>
      </ConfigErrorBoundary>
    );
  } catch (error) {
    console.error('Failed to start application:', error);
    // Error UI is already shown by the validation function
  }
}

// Start the application
startApplication();